import { Navigate, Route, Routes } from "react-router-dom";

import { lazy, Suspense } from "react";

import DashboardLayout from "./layouts/DashboardLayout.jsx";
import AuthLayout from "./layouts/AuthLayout.jsx";
import RequireAuth from "./layouts/RequireAuth.jsx";
import Login from "./pages/Login.jsx";
import Register from "./pages/Register.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import Users from "./pages/Users.jsx";
import Teams from "./pages/Teams.jsx";
import Projects from "./pages/Projects.jsx";
import ProductivityRules from "./pages/ProductivityRules.jsx";
import Schedules from "./pages/Schedules.jsx";
import Screenshots from "./pages/Screenshots.jsx";
import UserDetail from "./pages/UserDetail.jsx";
import Settings from "./pages/Settings.jsx";
import Profile from "./pages/Profile.jsx";
import SuperAdmin from "./pages/SuperAdmin.jsx";
import AppVersions from "./pages/AppVersions.jsx";
import DownloadApp from "./pages/DownloadApp.jsx";
import Documentation from "./pages/Documentation.jsx";
import Placeholder from "./pages/Placeholder.jsx";
import NotFound from "./pages/NotFound.jsx";
import { landingRouteFor, useAuth } from "@/context/auth.js";

// The roles that belong to an organization — see NAV_GROUPS in lib/nav.js.
const ORG_ROLES = ["admin", "manager", "employee"];

/** "/" goes where this role can actually work. */
function LandingRedirect() {
    const { user } = useAuth();
    return <Navigate to={landingRouteFor(user?.role)} replace />;
}

/*
 * The design-system review route is dev-only. Lazily imported behind
 * import.meta.env.DEV so the whole page — and the sample data in it — is tree
 * shaken out of the production bundle rather than merely being unreachable.
 */
const DesignSystem = import.meta.env.DEV ? lazy(() => import("./pages/DesignSystem.jsx")) : null;

/*
 * Route tree.
 *
 * Paths are IDENTICAL to the Next.js app's — bookmarks, links in the Bengali
 * docs and anything the desktop app opens must keep working. Do not "tidy"
 * /organization into /settings or /tracking-schedule into /schedules.
 *
 * Phase 0b: every route resolves and the shell is navigable, with placeholder
 * page bodies. Each later phase swaps one <Placeholder> for the real page.
 */

export default function App() {
    return (
        <Routes>
            {/* The Next.js app did this with a permanent redirect in next.config.js */}
            {/* Role-aware: a superAdmin has no organization, so /dashboard is a
                refusal for them. landingRouteFor already decides this after a
                sign-in; the bare "/" has to agree. */}
            <Route path="/" element={<LandingRedirect />} />

            <Route element={<AuthLayout />}>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
            </Route>

            {/* Everything below needs a session. DashboardLayout reads the user
                from AuthContext, so no props are threaded through. */}
            <Route element={<RequireAuth />}>
                <Route element={<DashboardLayout />}>
                    {/* Everything that resolves req.user.organization. A
                        superAdmin has none, so these are refused rather than
                        rendered empty — the same treatment the Platform routes
                        give an admin. */}
                    <Route element={<RequireAuth role={ORG_ROLES} />}>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/dashboard/:id" element={<UserDetail />} />
                        <Route path="/screenshots" element={<Screenshots />} />
                        <Route
                            path="/reports"
                            element={
                                <Placeholder
                                    title="Reports"
                                    breadcrumb={["Reports"]}
                                    markup="reports.html"
                                    phase="Phase 8d"
                                    notes={[
                                        "Ten ready report types plus four marked as planned.",
                                        "A report is a registry entry — stats, charts, table — rendered by one component.",
                                        "Replaces 23 files and roughly 9,300 lines.",
                                    ]}
                                />
                            }
                        />
                        <Route path="/users" element={<Users />} />
                        <Route path="/teams" element={<Teams />} />
                        <Route path="/projects" element={<Projects />} />
                        <Route path="/productivity-rules" element={<ProductivityRules />} />
                        <Route path="/tracking-schedule" element={<Schedules />} />
                        <Route path="/organization" element={<Settings />} />
                        <Route path="/download-app" element={<DownloadApp />} />
                    </Route>

                    {/* Not organization-scoped: your own account, and the docs. */}
                    <Route path="/profile" element={<Profile />} />
                    {/* Nested guard: signed in (outer) AND superAdmin (here). The
                        Next.js app only hid the nav item, so typing the URL still
                        rendered the page. */}
                    <Route element={<RequireAuth role="superAdmin" />}>
                        <Route path="/super-admin" element={<SuperAdmin />} />
                        <Route path="/app-versions" element={<AppVersions />} />
                    </Route>
                    <Route path="/documentation" element={<Documentation />} />

                    {DesignSystem && (
                        <Route
                            path="/design"
                            element={
                                <Suspense
                                    fallback={
                                        <div className="p-6 text-[13px] text-slate-400">
                                            Loading…
                                        </div>
                                    }
                                >
                                    <DesignSystem />
                                </Suspense>
                            }
                        />
                    )}

                    <Route path="*" element={<NotFound />} />
                </Route>
            </Route>
        </Routes>
    );
}
